import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { clearAdminToken, getAdminToken } from "../admin/auth.js";
import ProductManager from "../admin/ProductManager.js";
import CandleManager from "../admin/CandleManager.js";
import SiteSettingsManager from "../admin/SiteSettingsManager.js";
import LegalTermsManager from "../admin/LegalTermsManager.js";
import TestimonialManager from "../admin/TestimonialManager.js";
import ContactInquiryManager from "../admin/ContactInquiryManager.js";
import ReservationManager from "../admin/ReservationManager.js";
import OrderManager from "../admin/OrderManager.js";
import BuildOrderManager from "../admin/BuildOrderManager.js";
import "../Style/Admin.css";

function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    const token = getAdminToken();

    try {
      setError("");
      const [
        admin,
        products,
        contacts,
        reservations,
        settings,
        orders,
        testimonials,
      ] = await Promise.all([
        api.admin.me(token),
        api.admin.products.list({ page: 1, limit: 100 }, token),
        api.admin.contactInquiries.list({ page: 1, limit: 100 }, token),
        api.admin.reservations.list({ page: 1, limit: 100 }, token),
        api.admin.siteSettings.get(token),
        api.admin.orders.list({ page: 1, limit: 100 }, token),
        api.admin.testimonials.list(token),
      ]);
      let candles = { data: [] };
      let candlesError = "";
      let buildOrders = { data: [] };
      let buildOrdersError = "";

      try {
        candles = await api.candles.list();
      } catch (requestError) {
        candlesError = requestError.message || "Candles could not be loaded";
      }

      try {
        buildOrders = await api.admin.buildOrders.list(
          { page: 1, limit: 100 },
          token,
        );
      } catch (requestError) {
        if (requestError.status === 401 || requestError.status === 403) {
          throw requestError;
        }
        buildOrdersError =
          requestError.message || "Build orders could not be loaded";
      }

      setDashboard({
        admin: admin.data,
        products: products.data,
        candles: candles.data,
        candlesError,
        contacts: contacts.data,
        reservations: reservations.data,
        settings: settings.data,
        orders: orders.data,
        buildOrders: buildOrders.data || [],
        buildOrdersError,
        testimonials: testimonials.data,
      });
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(requestError.message || "Could not load the dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const handleUnauthorized = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  if (!dashboard && !error) {
    return <div className="admin-route-loading">Loading dashboard…</div>;
  }

  return (
    <main className="admin-dashboard">
      <header className="admin-dashboard-header">
        <div>
          <p className="admin-eyebrow">Private administration</p>
          <h1>Admin dashboard</h1>
          {dashboard && <span>{dashboard.admin.email}</span>}
        </div>
        <div className="admin-header-actions">
          <button type="button" onClick={loadDashboard}>
            Refresh
          </button>
          <button type="button" className="admin-logout" onClick={logout}>
            Log out
          </button>
        </div>
      </header>

      {error ? (
        <section className="admin-dashboard-error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadDashboard}>
            Try again
          </button>
        </section>
      ) : (
        <>
          <section className="admin-stat-grid" aria-label="Dashboard summary">
            <article>
              <span>Products</span>
              <strong>{dashboard.products.length}</strong>
            </article>
            <article>
              <span>Candles</span>
              <strong>{dashboard.candles.length}</strong>
            </article>
            <article>
              <span>Reservations</span>
              <strong>{dashboard.reservations.length}</strong>
            </article>
            <article>
              <span>Orders</span>
              <strong>{dashboard.orders.length}</strong>
            </article>
            <article>
              <span>Build orders</span>
              <strong>{dashboard.buildOrders.length}</strong>
            </article>
          </section>

          <ProductManager
            products={dashboard.products}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          <CandleManager
            candles={dashboard.candles}
            loadError={dashboard.candlesError}
            products={dashboard.products}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          <SiteSettingsManager
            settings={dashboard.settings}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          <LegalTermsManager onUnauthorized={handleUnauthorized} />

          <TestimonialManager
            testimonials={dashboard.testimonials}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          {/* <ContactInquiryManager
            contacts={dashboard.contacts}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          /> */}

          <ReservationManager
            reservations={dashboard.reservations}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          <OrderManager
            orders={dashboard.orders}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />

          <BuildOrderManager
            buildOrders={dashboard.buildOrders}
            loadError={dashboard.buildOrdersError}
            onChanged={loadDashboard}
            onUnauthorized={handleUnauthorized}
          />
        </>
      )}
    </main>
  );
}

export default AdminDashboard;
